<template>
  <div class="message-composer">
    <!-- Formatierungs-Toolbar (aus NewMessageDialog extrahiert, damit
         Verfassen-Dialog und Unterhaltungs-Fenster denselben Editor teilen) -->
    <div v-if="toolbar" class="mail-editor-toolbar">
      <select
        class="mail-toolbar-select"
        :title="$gt('Schriftart')"
        @change="exec('fontName', $event.target.value); $event.target.value = ''"
      >
        <option value="" disabled selected>{{ $gt('Schriftart') }}</option>
        <option value="Arial">{{ $gt('Arial') }}</option>
        <option value="Helvetica">{{ $gt('Helvetica') }}</option>
        <option value="Georgia">{{ $gt('Georgia') }}</option>
        <option value="Times New Roman">{{ $gt('Times New Roman') }}</option>
        <option value="Courier New">{{ $gt('Courier New') }}</option>
        <option value="Verdana">{{ $gt('Verdana') }}</option>
      </select>
      <select
        class="mail-toolbar-select mail-toolbar-select--sm"
        :title="$gt('Schriftgröße')"
        @change="exec('fontSize', $event.target.value); $event.target.value = ''"
      >
        <option value="" disabled selected>{{ $gt('Größe') }}</option>
        <option value="1">{{ $gt('Klein') }}</option>
        <option value="3">{{ $gt('Normal') }}</option>
        <option value="5">{{ $gt('Groß') }}</option>
        <option value="7">{{ $gt('Sehr groß') }}</option>
      </select>
      <div class="mail-toolbar-sep"></div>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Fett')" @click="exec('bold')"><Bold :size="15" /></button>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Kursiv')" @click="exec('italic')"><Italic :size="15" /></button>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Unterstrichen')" @click="exec('underline')"><Underline :size="15" /></button>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Durchgestrichen')" @click="exec('strikeThrough')"><Strikethrough :size="15" /></button>
      <div class="mail-toolbar-sep"></div>
      <!-- Kundenfeedback 24.10.2026 (IV.8): Farbfelder statt nativem
           Farbwähler – der nahm dem Editor die Markierung (siehe
           utils/editorHighlight). -->
      <EditorColorMenu
        :title="$gt('Schriftfarbe')"
        :colors="textColors"
        :current="textColor"
        testid="composer-text-color"
        @open="rememberSelection"
        @pick="applyTextColor"
      >
        <Type :size="14" />
      </EditorColorMenu>
      <EditorColorMenu
        :title="$gt('Texthervorhebungsfarbe (Text farbig hinterlegen)')"
        :colors="highlightColors"
        :current="highlightColor"
        :none-label="$gt('Keine Hervorhebung')"
        testid="composer-highlight"
        @open="rememberSelection"
        @pick="applyHighlight"
      >
        <Highlighter :size="14" />
      </EditorColorMenu>
      <div class="mail-toolbar-sep"></div>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Linksbündig')" @click="exec('justifyLeft')"><AlignLeft :size="15" /></button>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Zentriert')" @click="exec('justifyCenter')"><AlignCenter :size="15" /></button>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Rechtsbündig')" @click="exec('justifyRight')"><AlignRight :size="15" /></button>
      <div class="mail-toolbar-sep"></div>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Aufzählung')" @click="exec('insertUnorderedList')"><List :size="15" /></button>
      <button type="button" class="mail-toolbar-btn" @mousedown.prevent :title="$gt('Nummerierung')" @click="exec('insertOrderedList')"><ListOrdered :size="15" /></button>
    </div>

    <!-- Editor -->
    <div
      ref="editor"
      class="mail-editor mail-editor--compose"
      contenteditable="true"
      :data-placeholder="placeholder"
      @input="onInput"
      @paste="onPaste"
      @keydown="onKeydown"
      @focus="$emit('focus')"
    ></div>
  </div>
</template>

<script>
import {
  Bold, Italic, Underline, Strikethrough, Type, Highlighter,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
} from "lucide-vue-next";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { applyBackgroundColor, HIGHLIGHT_COLORS, isSafeEditorColor, TEXT_COLORS } from "@/utils/editorHighlight";
import EditorColorMenu from "./EditorColorMenu.vue";

/**
 * Wiederverwendbarer Rich-Text-Composer (Formatierungs-Toolbar +
 * contenteditable-Editor), extrahiert aus NewMessageDialog. Wird sowohl im
 * Verfassen-Dialog als auch beim Antworten (Antwortfeld der Korrespondenz,
 * schwebendes Unterhaltungs-Fenster) verwendet (Kundenfeedback 30.08.26 und
 * 25.10.2026 Teil 3, I: gleiche Editor-Möglichkeiten beim Antworten).
 *
 * v-model: HTML-Inhalt des Editors. Strg+Enter löst das "send"-Event aus.
 */
export default {
  name: "MessageComposer",

  components: {
    EditorColorMenu,
    Bold, Italic, Underline, Strikethrough, Type, Highlighter,
    AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
  },

  props: {
    modelValue: { type: String, default: "" },
    placeholder: { type: String, default: "Nachricht verfassen…" },
    /** Formatierungsleiste zeigen (das Antwortfeld blendet sie erst beim Schreiben ein). */
    toolbar: { type: Boolean, default: true },
  },

  emits: ["update:modelValue", "send", "focus"],

  data() {
    return {
      highlightColors: HIGHLIGHT_COLORS,
      textColors: TEXT_COLORS,
      // Zuletzt gewählte Farben (Anzeige im Toolbar-Knopf).
      highlightColor: HIGHLIGHT_COLORS[0].value,
      textColor: TEXT_COLORS[0].value,
    };
  },

  watch: {
    /**
     * Externe Änderungen (Entwurf laden, Signatur-Refresh) in den Editor
     * spiegeln. Identischer Inhalt wird übersprungen, damit die Cursor-
     * Position beim normalen Tippen erhalten bleibt.
     */
    modelValue(value) {
      const el = this.$refs.editor;
      if (el && el.innerHTML !== value) el.innerHTML = value || "";
    },
  },

  mounted() {
    if (this.$refs.editor) this.$refs.editor.innerHTML = this.modelValue || "";
  },

  methods: {
    /** Das rohe contenteditable-Element (für Signatur-/Entwurfs-Logik). */
    editorEl() {
      return this.$refs.editor || null;
    },

    focus() {
      this.$refs.editor?.focus();
    },

    /** Nach direkter DOM-Manipulation (z. B. Signaturtausch) synchronisieren. */
    syncFromEditor() {
      this.onInput();
    },

    /**
     * Merkt sich die aktuelle Cursorposition/Auswahl im Editor, bevor ein
     * Dialog (z. B. Textbausteine) den Fokus übernimmt. insertHtml() fügt
     * später genau dort ein.
     */
    saveSelection() {
      const el = this.$refs.editor;
      const sel = window.getSelection();
      this._savedRange = null;
      if (!el || !sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) {
        this._savedRange = range.cloneRange();
      }
    },

    /**
     * Fügt HTML an der (gemerkten) Cursorposition ein. Liegt keine
     * Cursorposition im Editor vor, wird ans Ende des Textes eingefügt –
     * oberhalb der Signatur (Inhaber-Feedback 15.08.26, I.4 Textbausteine).
     */
    insertHtml(rawHtml) {
      const el = this.$refs.editor;
      // Fragmente kommen aus gespeicherten Textbausteinen (auch fremden
      // Unternehmens-Bausteinen) – vor insertHTML auf die Editor-
      // Positivliste reduzieren, damit kein Markup Code ausführt.
      const html = sanitizeHtml(rawHtml);
      if (!el || !html) return;

      let range = this._savedRange;
      this._savedRange = null;
      if (range && !el.contains(range.commonAncestorContainer)) range = null;
      if (!range) {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const r = sel.getRangeAt(0);
          if (el.contains(r.commonAncestorContainer)) range = r.cloneRange();
        }
      }

      if (range) {
        el.focus();
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand("insertHTML", false, html);
        this.onInput();
        return;
      }

      // Ohne Cursorposition: ans Ende oberhalb der Signatur (samt der
      // Leerzeilen davor), sonst ans Ende des Editors.
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;
      const frag = document.createDocumentFragment();
      while (wrapper.firstChild) frag.appendChild(wrapper.firstChild);

      const sig = el.querySelector('[data-signature="true"]');
      if (sig) {
        let anchor = sig;
        let prev = sig.previousSibling;
        while (prev && prev.nodeName === "BR") {
          anchor = prev;
          prev = prev.previousSibling;
        }
        el.insertBefore(frag, anchor);
      } else {
        el.appendChild(frag);
      }
      el.focus();
      this.onInput();
    },

    /** Markierung merken, bevor eine Farbauswahl geöffnet wird. */
    rememberSelection() {
      const el = this.$refs.editor;
      const sel = window.getSelection();
      this._colorRange = null;
      if (!el || !sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) this._colorRange = range.cloneRange();
    },

    /** Gemerkte Markierung wiederherstellen (z. B. nach dem nativen Farbwähler). */
    restoreSelection() {
      const el = this.$refs.editor;
      if (!el) return null;
      el.focus();
      const sel = window.getSelection();
      const saved = this._colorRange;
      if (sel && saved && el.contains(saved.commonAncestorContainer)) {
        sel.removeAllRanges();
        sel.addRange(saved);
      }
      return sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
    },

    /** Text farbig hinterlegen (IV.8). */
    applyHighlight(color) {
      if (!isSafeEditorColor(color)) return;
      const range = this.restoreSelection();
      if (applyBackgroundColor(document, range, color)) {
        if (color !== "transparent") this.highlightColor = color;
        this.onInput();
      }
    },

    applyTextColor(color) {
      if (!isSafeEditorColor(color) || color === "transparent") return;
      this.restoreSelection();
      document.execCommand("foreColor", false, color);
      this.textColor = color;
      this.onInput();
    },

    exec(command, value) {
      this.$refs.editor?.focus();
      document.execCommand(command, false, value || null);
      this.onInput();
    },

    onInput() {
      this.$emit("update:modelValue", this.$refs.editor?.innerHTML || "");
    },

    onPaste(e) {
      e.preventDefault();
      const html = e.clipboardData.getData("text/html");
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertHTML", false, html || text);
      this.onInput();
    },

    /** Strg/Cmd+Enter sendet (Verhalten des bisherigen Antwort-Textfelds). */
    onKeydown(e) {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.$emit("send");
      }
    },
  },
};
</script>

<style scoped>
.message-composer {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

/* ── Toolbar (Stile aus NewMessageDialog übernommen) ── */
.mail-editor-toolbar {
  display: flex; align-items: center; gap: 2px;
  padding: 6px 12px;
  border-bottom: 1px solid #eef0f3;
  flex-wrap: wrap;
  background: #fafbfc;
  flex-shrink: 0;
}
.mail-toolbar-btn {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none; border-radius: 6px;
  background: none; color: var(--grey-color);
  cursor: pointer; transition: all 0.1s;
  position: relative;
}
.mail-toolbar-btn:hover { background: var(--grey-light-color); color: var(--dark-color); }
.mail-toolbar-select {
  padding: 4px 6px;
  border: 1px solid var(--border-grey-color);
  border-radius: 6px;
  font-size: 0.75rem; color: var(--grey-color);
  background: #fff; cursor: pointer; outline: none;
}
.mail-toolbar-select--sm { width: 70px; }
.mail-toolbar-sep {
  width: 1px; height: 20px;
  background: var(--border-grey-color); margin: 0 4px;
}

/* ── Editor: füllt die verfügbare Höhe (Fenster-Layouts) ── */
.mail-editor {
  flex: 1 1 auto;
  min-height: 100px;
  overflow-y: auto;
  padding: 12px 16px;
  font-size: 0.88rem; line-height: 1.55;
  color: var(--dark-color); outline: none; font-family: inherit;
}
.mail-editor--compose:empty::before {
  content: attr(data-placeholder);
  color: var(--border-grey-color);
  pointer-events: none;
}
.mail-editor :deep(.mail-signature) {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-grey-color);
  color: var(--grey-color);
}
/* Nicht befüllbare Platzhalter einer eingefügten E-Mail-Vorlage: sichtbar
   markiert, damit sie vor dem Versand ergänzt oder entfernt werden. */
.mail-editor :deep(.mail-tpl-missing) {
  padding: 0 3px;
  border-radius: 4px;
  background: #fef3c7;
  color: #92400e;
  border-bottom: 2px dotted #f59e0b;
}
</style>
